// cropBox — 自動クロップ (growth-timelapse-share-2026-07-02.md §5.2
// 「自動クロップ ✅ MVP (lite): 選択範囲内の変化ピクセル外接矩形に自動で
// 寄せる (+余白、OFF トグル)」, T2 slice).
//
// Instead of diffing pixels (which would force a second replay pass), the
// changed region is derived **from the event geometry** — the same trimmed
// event list the replay applies:
//
//   - `stroke.segment` points, each expanded by the stroke's radius
//     (`stroke.start.width / 2`) + a 1 px AA margin. Segments whose
//     `stroke.start` fell outside the trim range are ignored — the replay
//     no-ops them too (drawing-core drops orphan segments), so they can't
//     have painted anything.
//   - `whiteboard.imagepaste` rectangles (spec'd in even though today's
//     replay skips pastes — when the re-sign slice lands, the crop is
//     already correct; until then it only ever *widens* the box).
//   - `whiteboard.clear` **全消し** (`layerId === undefined`) = **box reset**.
//     A full wipe blanks the canvas, so only what is drawn *after* it is
//     visible in the final frames — the crop must follow the post-clear
//     drawing, not the whole canvas. Earlier fix: the old code `return`ed
//     the full canvas here, so any session containing a 全消し (nearly all
//     of them) silently disabled cropping. We now clear the accumulated
//     bounding box and keep scanning (實機バグ修正 2026-07-03).
//   - `whiteboard.clear` **単一レイヤ** (`layerId !== undefined`) = skip —
//     it wipes one layer but the others still hold their strokes, so it
//     must NOT reset the whole-canvas box (that would drop everything).
//   - `whiteboard.background-set` = skip — a background recolor does not
//     constrain the drawn region (the crop is about where the strokes are;
//     the bg fills whatever rect the crop ends up being).
//
// If the box is still empty after the last 全消し (a wipe with nothing
// drawn after it, or a strokeless log) the tight box is degenerate and the
// crop falls back to the full canvas (`!Number.isFinite(minX)`).
//
// The raw box is then padded, clamped to the canvas, grown to a minimum
// edge (a 3-stroke doodle on a 4000² canvas should not produce a 40 px
// video), and rounded to **even** dimensions (H.264 4:2:0 — same policy as
// `computeOutputSize`, applied here too so the crop→scale chain never
// starts from an odd source box).
//
// Everything in this module is pure (no canvas, no engine) — unit-tested
// in the vitest node env per the T2 test strategy (jsdom に WebCodecs は
// 無い → pure 部分を unit test).

import type { StrokeLogEvent } from '@oekaki/protocol';
import type { FrameRgba, FrameSize } from './frameScale.js';

/** Integer pixel rect inside the room canvas. `width`/`height` even (best effort). */
export interface CropRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** Breathing room around the tight bounding box (§5.2 「+余白」). */
export const DEFAULT_CROP_PAD_PX = 16;

/**
 * Minimum output-box edge. A tiny doodle still yields a watchable video
 * instead of a hyper-zoomed pixel soup; capped at the canvas size when the
 * canvas itself is smaller.
 */
export const DEFAULT_CROP_MIN_EDGE_PX = 320;

/**
 * Extra radius added to every stroke point beyond `width / 2`: the AA
 * path's analytic coverage can touch one pixel beyond the geometric
 * radius, and rounding at rasterize time can add another. 2 px keeps the
 * box safely conservative for both stamp paths.
 */
export const STROKE_RADIUS_MARGIN_PX = 2;

export interface ChangedRegionOptions {
  /** Default {@link DEFAULT_CROP_PAD_PX}. */
  readonly padPx?: number;
  /** Default {@link DEFAULT_CROP_MIN_EDGE_PX}. */
  readonly minEdgePx?: number;
}

/** Full-canvas rect (still even-rounded — see module header). */
export const fullCanvasRect = (canvas: FrameSize): CropRect => ({
  x: 0,
  y: 0,
  width: evenFloor(canvas.width),
  height: evenFloor(canvas.height),
});

/** `true` when `rect` covers the whole canvas (⇒ cropping is a no-op). */
export const isFullCanvasRect = (rect: CropRect, canvas: FrameSize): boolean => {
  const full = fullCanvasRect(canvas);
  return (
    rect.x === full.x &&
    rect.y === full.y &&
    rect.width === full.width &&
    rect.height === full.height
  );
};

/**
 * Bounding box of everything the (trimmed) event list visibly changes,
 * measured from the **last 全消し** (`whiteboard.clear` with no `layerId`)
 * onward — a full wipe blanks the canvas, so nothing painted before it
 * survives into the final frames. Returns the full canvas when the list is
 * empty, when nothing is drawn after the last wipe, or when nothing
 * otherwise constrains the box — "no crop" is always expressed as the
 * full-canvas rect, never as `null`, so callers have a single shape to
 * thread into the replay.
 */
export const computeChangedRegionBox = (
  events: readonly StrokeLogEvent[],
  canvas: FrameSize,
  options: ChangedRegionOptions = {},
): CropRect => {
  if (canvas.width < 2 || canvas.height < 2) {
    throw new TypeError(`computeChangedRegionBox: invalid canvas ${canvas.width}x${canvas.height}`);
  }
  const pad = options.padPx ?? DEFAULT_CROP_PAD_PX;
  const minEdge = options.minEdgePx ?? DEFAULT_CROP_MIN_EDGE_PX;

  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  const radiusBySid = new Map<string, number>();

  for (const ev of events) {
    switch (ev.t) {
      case 'stroke.start':
        radiusBySid.set(ev.sid, ev.width / 2 + STROKE_RADIUS_MARGIN_PX);
        break;
      case 'stroke.segment': {
        const r = radiusBySid.get(ev.sid);
        // Orphan segment (start trimmed away / lost in a gap): the replay
        // no-ops it, so it changes nothing — skip (see module header).
        if (r === undefined) break;
        for (const pt of ev.pts) {
          if (pt.x - r < minX) minX = pt.x - r;
          if (pt.x + r > maxX) maxX = pt.x + r;
          if (pt.y - r < minY) minY = pt.y - r;
          if (pt.y + r > maxY) maxY = pt.y + r;
        }
        break;
      }
      case 'stroke.end':
        break;
      case 'whiteboard.imagepaste':
        if (ev.x < minX) minX = ev.x;
        if (ev.y < minY) minY = ev.y;
        if (ev.x + ev.width > maxX) maxX = ev.x + ev.width;
        if (ev.y + ev.height > maxY) maxY = ev.y + ev.height;
        break;
      case 'whiteboard.region': {
        // 範囲の移動・コピー: 動く絵の外接矩形 = 置き先、move なら消える元の範囲も映る。
        const rects = [{ x: ev.dst.x, y: ev.dst.y }];
        if (ev.mode === 'move') rects.push({ x: ev.src.x, y: ev.src.y });
        for (const r of rects) {
          if (r.x < minX) minX = r.x;
          if (r.y < minY) minY = r.y;
          if (r.x + ev.src.w > maxX) maxX = r.x + ev.src.w;
          if (r.y + ev.src.h > maxY) maxY = r.y + ev.src.h;
        }
        break;
      }
      case 'whiteboard.clear':
        // 全消し (layerId 無し) = 以降だけが動画に映る → 蓄積済みの外接矩形を
        // リセットして以降で再蓄積 (return しない)。単一レイヤ clear は他レイヤの
        // 描画が残るので全面リセットせず skip。(module header 参照)
        if (ev.layerId === undefined) {
          minX = Number.POSITIVE_INFINITY;
          minY = Number.POSITIVE_INFINITY;
          maxX = Number.NEGATIVE_INFINITY;
          maxY = Number.NEGATIVE_INFINITY;
        }
        break;
      case 'whiteboard.background-set':
        // 背景色変更は描画領域を制約しない → 矩形に寄与させず skip。
        break;
      case 'whiteboard.restore':
        // 系譜マーカー (§4.5-2)。clear のような box リセットは**しない**:
        // 置換後の内容 (系譜範囲のイベント) は seq 上マーカーより前に並ぶため、
        // ここでリセットすると土台の寄与が箱から落ちる。系譜対応の材料構成
        // (系譜範囲 root→leaf → マーカー以降、の順に並べる) は encodeJob 側の
        // `collectFinalDocumentRanges` が担う。
        break;
    }
  }

  if (!Number.isFinite(minX)) return fullCanvasRect(canvas);

  // Pad → clamp to canvas.
  let x0 = Math.max(0, Math.floor(minX - pad));
  let y0 = Math.max(0, Math.floor(minY - pad));
  let x1 = Math.min(canvas.width, Math.ceil(maxX + pad));
  let y1 = Math.min(canvas.height, Math.ceil(maxY + pad));
  if (x1 <= x0 || y1 <= y0) return fullCanvasRect(canvas); // degenerate (all points off-canvas)

  // Minimum edge — grow around the box center, then shove back inside.
  [x0, x1] = growSpan(x0, x1, canvas.width, minEdge);
  [y0, y1] = growSpan(y0, y1, canvas.height, minEdge);

  // Even dimensions (prefer growing; shrink only when pinned at both borders).
  const [ex, width] = evenizeSpan(x0, x1, canvas.width);
  const [ey, height] = evenizeSpan(y0, y1, canvas.height);

  return { x: ex, y: ey, width, height };
};

/**
 * Extract `rect` out of a straight-α RGBA buffer (`sw × sh`). Row-wise
 * `subarray` + `set` copies — O(rect area). Returns the input buffer
 * itself when the rect covers the full source (identity fast-path, same
 * caller contract as `FrameScaler`: frames are consumed before the next
 * one is produced, so no defensive copy).
 *
 * Throws `TypeError` when the rect leaves the source bounds — the crop
 * computer always clamps, so an out-of-bounds rect is a caller bug.
 */
export const cropRgba = (src: FrameRgba, sw: number, sh: number, rect: CropRect): FrameRgba => {
  if (src.length !== sw * sh * 4) {
    throw new TypeError(`cropRgba: src length ${src.length} != ${sw}x${sh}x4`);
  }
  if (
    !Number.isInteger(rect.x) ||
    !Number.isInteger(rect.y) ||
    !Number.isInteger(rect.width) ||
    !Number.isInteger(rect.height) ||
    rect.width <= 0 ||
    rect.height <= 0 ||
    rect.x < 0 ||
    rect.y < 0 ||
    rect.x + rect.width > sw ||
    rect.y + rect.height > sh
  ) {
    throw new TypeError(
      `cropRgba: rect ${rect.x},${rect.y} ${rect.width}x${rect.height} out of ${sw}x${sh}`,
    );
  }
  if (rect.x === 0 && rect.y === 0 && rect.width === sw && rect.height === sh) return src;

  const out = new Uint8ClampedArray(rect.width * rect.height * 4);
  const rowBytes = rect.width * 4;
  for (let dy = 0; dy < rect.height; dy++) {
    const srcOff = ((rect.y + dy) * sw + rect.x) * 4;
    out.set(src.subarray(srcOff, srcOff + rowBytes), dy * rowBytes);
  }
  return out;
};

// ─── Span helpers ────────────────────────────────────────────────────────────

/** Grow `[v0, v1)` to at least `minLen` (capped at `[0, limit]`), centered. */
const growSpan = (v0: number, v1: number, limit: number, minLen: number): [number, number] => {
  const len = v1 - v0;
  const want = Math.min(Math.max(minLen, len), limit);
  if (want <= len) return [v0, v1];
  const extra = want - len;
  let n0 = v0 - Math.floor(extra / 2);
  let n1 = v1 + Math.ceil(extra / 2);
  if (n0 < 0) {
    n1 -= n0; // shift right by the overhang
    n0 = 0;
  }
  if (n1 > limit) {
    n0 -= n1 - limit; // shift left by the overhang
    n1 = limit;
  }
  return [Math.max(0, n0), n1];
};

/**
 * Make the span's length even: prefer extending right, then left; shrink
 * only when pinned at both borders. Returns `[start, evenLength]`.
 */
const evenizeSpan = (v0: number, v1: number, limit: number): [number, number] => {
  const len = v1 - v0;
  if (len % 2 === 0) return [v0, len];
  if (v1 < limit) return [v0, len + 1];
  if (v0 > 0) return [v0 - 1, len + 1];
  return [v0, Math.max(2, len - 1)];
};

const evenFloor = (v: number): number => Math.max(2, Math.floor(v / 2) * 2);
